import * as React from "react";
import { CircleDashed } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";

/**
 * Phase H5d — banner rendered above the assessment-domain pages when
 * the SA has explicitly paused authoring for the tenant
 * (`feature_flags.assessments_authoring_by_tenant === false`).
 *
 * Visual posture: informational, not destructive. The IA isn't being
 * punished — the tenant capability switch is owned by the SA / account
 * manager, and reads stay fully available. We use the muted info tone
 * (border + amber accent) rather than the destructive red so the
 * banner doesn't read as an error every time an admin loads the page.
 *
 * Behaviour:
 * - Renders only when `useCanAuthorAssessments()` reports
 *   `pausedByTenant === true` (RBAC granted, flag disabled).
 * - The trigger / iconography matches the muted "system status" tone
 *   used by other Phase B2-era banners (see `FeatureFlagsCard` copy in
 *   the SA portal for the canonical wording).
 */
export interface TenantAuthoringPausedBannerProps {
  className?: string;
  /**
   * Optional override for the body copy. Defaults to the canonical
   * H5d wording — only override when a specific call site needs to
   * differentiate (e.g. the Detail page vs. the list page).
   */
  body?: React.ReactNode;
}

export function TenantAuthoringPausedBanner({ className, body }: TenantAuthoringPausedBannerProps) {
  return (
    <div
      role="status"
      data-testid="assessments-authoring-paused-banner"
      className={cn(
        "border-amber-500/30 bg-amber-500/5 text-amber-900 dark:text-amber-200",
        "flex items-start gap-2 rounded-md border px-3 py-2.5 text-xs",
        className,
      )}
    >
      <CircleDashed
        aria-hidden="true"
        className="mt-0.5 size-3.5 shrink-0 text-amber-600 dark:text-amber-300"
      />
      <div className="flex-1 leading-relaxed">
        {body ?? (
          <>
            <span className="font-medium">Assessment authoring is paused.</span> Your account
            manager has temporarily disabled creating, editing, or activating assessment templates
            for your institution. You can still browse the existing question bank + templates and
            run admission flows. Contact your account manager to re-enable authoring.
          </>
        )}
      </div>
    </div>
  );
}
